<template>
  <Grid
    class="chometz-submissions-grid"
    :data-items="filteredSubmissions"
    :columns="columns"
    :loader="loading"
    ref="gridRef"
    data-cy="chometz-submissions-grid"
  >
    <template v-slot:TypeCell="{ props: { dataItem } }">
      <td class="k-table-td">
        {{ getSubmissionTypeString(dataItem) }}
      </td>
    </template>
    <template v-slot:CheckCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        <font-awesome-icon
          class="check-icon"
          v-if="dataItem[field]"
          icon="check"
        />
      </td>
    </template>
  </Grid>
</template>

<script setup lang="ts">
import { Grid } from "@progress/kendo-vue-grid";
import { computed, onMounted, ref, watch } from "vue";
import type { ChometzSubmission } from "@/Domain/ChometzSubmission";
import {
  getSubmissionTypeString,
  isDifferent,
} from "@/shared/helpers/chometzHelper";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import GridController from "@/shared/services/GridController";

const props = defineProps<{
  submissions: ChometzSubmission[];
  expanded?: boolean;
}>();

const gridRef = ref<InstanceType<typeof Grid> | null>(null);
const loading = ref(false);

const columnFields = computed(
  () =>
    columns.value
      .filter((x) => x.field != "Date")
      .map((x) => x.field) as (keyof ChometzSubmission)[]
);

const filteredSubmissions = computed(() => {
  return props.submissions.filter((item, index) => {
    return isDifferent(props.submissions[index - 1], item, columnFields.value);
  });
});

const columns = ref<GridColumnPropsExtended[]>([
  {
    title: "Date Submitted",
    field: "SubmittedDate",
    type: "date",
    format: "{0:MM/dd/yyyy}",
    width: 90,
  },
  {
    title: "Name",
    field: "SubmissionCustomerName",
    minWidth: 120,
  },
  {
    title: "Sold through",
    cell: "TypeCell",
    field: "SubmissionType",
    minWidth: 120,
  },
  {
    title: "Comment",
    field: "SubmissionComment",
    minWidth: 120,
  },
  {
    title: "Time zone",
    field: "CustomerTimezone",
    minWidth: 120,
  },
  {
    title: "No Chometz",
    cell: "CheckCell",
    field: "NoChometz",
    minWidth: 80,
  },
  {
    title: "Finalized",
    cell: "CheckCell",
    field: "SubmissionFinalized",
    minWidth: 80,
  },
  {
    title: "Date",
    field: "Date",
    type: "date",
    format: "{0:MM/dd/yyyy h:mm:ss a}",
    width: 90,
  },
]);

const controller = new GridController<ChometzSubmission>(
  "ChometzSubmissionsGrid",
  columns.value
);

controller.initOptions();

watch(
  () => props.expanded,
  () => {
    loading.value = true;
    setTimeout(() => {
      controller.handleResize();
      loading.value = false;
    }, 500);
  }
);

onMounted(() => {
  controller.setGridRef(gridRef);
});
</script>

<style lang="scss">
.chometz-submissions-grid {
  .k-column-title {
    padding-right: 0 !important;
  }
}
</style>

<style scoped lang="scss">
:deep(th.k-table-th.k-header) {
  background: #f8f9fa;
  font-weight: bold;
  font-size: 12px;
}

.chometz-submissions-grid {
  height: auto;
  max-height: 100%;

  .check-icon {
    width: 100%;
  }
}
</style>
